import { API_BASE_URL } from "../../contexts/ApiContext";
import {
  categoryToSlug,
  estimateReadingTime,
  excerptFromBlocks,
  excerptFromMarkdown,
  markdownToBlocks,
  slugify,
} from "./blogUtils";

export const BLOG_PAGE_SIZE = 21;

const DEFAULT_AUTHOR = "Команда Энилекс";
const DEFAULT_CATEGORY_DESCRIPTION =
  "Практические материалы по аналитике, рекламе, SEO и управлению продажами на маркетплейсах.";
const DEFAULT_OG_IMAGE_PATH = "/brand/anylex-logo-cyr.png";

const CATEGORY_CATALOG = [
  {
    slug: "marketplace-operations",
    name: "Операции на маркетплейсах",
    description:
      "Контроль карточек, контента, отзывов, возвратов и ежедневной операционной работы seller-команды.",
    aliases: ["marketplace-operations", "операции на маркетплейсах", "операции"],
  },
  {
    slug: "analytics",
    name: "Аналитика",
    description:
      "Метрики, unit-экономика и контроль точек роста для команд маркетплейсов.",
    aliases: ["analytics", "analitika", "аналитика"],
  },
  {
    slug: "advertising",
    name: "Реклама",
    description:
      "Ozon Ads, управление ставками, ДРР и performance-механики для роста продаж.",
    aliases: ["advertising", "ads", "reklama", "реклама"],
  },
  {
    slug: "seo",
    name: "SEO",
    description:
      "SEO карточек, контент, поисковая видимость и рост органического трафика.",
    aliases: ["seo", "сео"],
  },
  {
    slug: "pricing",
    name: "Ценообразование",
    description:
      "Маржа, ценовые коридоры, промо и защита юнит-экономики без ручной паники.",
    aliases: ["pricing", "tsenoobrazovanie", "ценообразование"],
  },
  {
    slug: "logistics",
    name: "Логистика",
    description:
      "FBO, FBS, остатки, SLA поставок и логистика без просадки маржи.",
    aliases: ["logistics", "logistika", "логистика"],
  },
  {
    slug: "reviews",
    name: "Отзывы",
    description:
      "Работа с отзывами, рейтингом и поведенческими сигналами карточки товара.",
    aliases: ["reviews", "otzyvy", "отзывы"],
  },
  {
    slug: "product-management",
    name: "Управление товарами",
    description:
      "Ассортимент, карточки, сезонность и операционная работа с товарным каталогом.",
    aliases: ["product-management", "product", "товары", "управление товарами"],
  },
  {
    slug: "ai-automation",
    name: "AI-автоматизация",
    description:
      "Автоматизация маркетплейс-операций и контента с помощью AI-инструментов.",
    aliases: ["ai-automation", "ai", "автоматизация", "ai-автоматизация"],
  },
];

export const BLOG_CONTENT_TYPES = [
  { slug: "all", name: "Все" },
  { slug: "Новости", name: "Новости" },
  { slug: "Статьи", name: "Статьи" },
  { slug: "Инструкции", name: "Инструкции" },
];

const categoryAliasMap = CATEGORY_CATALOG.reduce((map, category) => {
  map.set(category.slug, category);
  category.aliases.forEach((alias) => {
    map.set(slugify(alias), category);
  });
  map.set(slugify(category.name), category);
  return map;
}, new Map());

const STATIC_CATEGORY_COLLECTION = CATEGORY_CATALOG.map((category) => ({
  slug: category.slug,
  name: category.name,
  description: category.description,
  count: 0,
}));

function getBlogSiteOrigin() {
  const configuredOrigin = (
    process.env.REACT_APP_BLOG_SITE_URL ||
    process.env.REACT_APP_PUBLIC_SITE_URL ||
    ""
  )
    .trim()
    .replace(/\/$/, "");

  if (configuredOrigin) {
    return configuredOrigin;
  }

  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    const isLocalHost = host === "localhost" || host === "127.0.0.1";
    if (isLocalHost) {
      return window.location.origin;
    }
  }

  return "https://seller.anylex.ru";
}

export const BLOG_SITE_ORIGIN = getBlogSiteOrigin();

export function buildBlogUrl(path = "/blog") {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${BLOG_SITE_ORIGIN}${normalizedPath}`;
}

export function resolveBlogImageUrl(pathOrUrl) {
  if (!pathOrUrl) {
    return buildBlogUrl(DEFAULT_OG_IMAGE_PATH);
  }
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  return buildBlogUrl(pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`);
}

export function resolveCategoryMeta(value) {
  if (!value) return null;
  const key = slugify(value);
  return categoryAliasMap.get(key) || null;
}

export function resolveCategorySlug(value) {
  return resolveCategoryMeta(value)?.slug || slugify(value || "");
}

function parseTags(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeCategoryName(value, fallbackSlug) {
  const categoryMeta = resolveCategoryMeta(value || fallbackSlug);
  if (categoryMeta) {
    return categoryMeta;
  }

  const rawValue = String(value || fallbackSlug || "Статьи").trim();
  return {
    slug: categoryToSlug(rawValue),
    name: rawValue,
    description: DEFAULT_CATEGORY_DESCRIPTION,
  };
}

export function normalizeBlogArticle(rawArticle) {
  if (!rawArticle?.slug || !rawArticle?.title) {
    return null;
  }

  const categoryMeta = normalizeCategoryName(
    rawArticle.category || rawArticle.category_name || rawArticle.section,
    rawArticle.category_slug || rawArticle.categorySlug
  );

  const blocks =
    Array.isArray(rawArticle.blocks) && rawArticle.blocks.length
      ? rawArticle.blocks
      : markdownToBlocks(rawArticle.content || rawArticle.markdown || "");

  const description =
    rawArticle.description ||
    excerptFromBlocks(blocks) ||
    excerptFromMarkdown(rawArticle.content || rawArticle.markdown || "");

  const publishedAt =
    rawArticle.published_at ||
    rawArticle.publishedAt ||
    rawArticle.date ||
    rawArticle.created_at ||
    rawArticle.createdAt ||
    "";

  const updatedAt =
    rawArticle.updated_at ||
    rawArticle.updatedAt ||
    publishedAt ||
    rawArticle.date ||
    "";

  const ogImage =
    rawArticle.og_image ||
    rawArticle.ogImage ||
    rawArticle.image ||
    DEFAULT_OG_IMAGE_PATH;

  return {
    slug: rawArticle.slug,
    title: rawArticle.title,
    description,
    hero: rawArticle.hero || rawArticle.lede || description,
    contentType: rawArticle.content_type || rawArticle.contentType || "Статьи",
    category: categoryMeta.name,
    categorySlug: categoryMeta.slug,
    categoryDescription: categoryMeta.description,
    tags: parseTags(rawArticle.tags),
    author: rawArticle.author || DEFAULT_AUTHOR,
    publishedAt,
    updatedAt,
    readingTime:
      Number(rawArticle.reading_time || rawArticle.readingTime) ||
      estimateReadingTime(blocks),
    ogImage,
    leadMagnet: rawArticle.lead_magnet || rawArticle.leadMagnet || "trial",
    contentFormat: rawArticle.content_format || rawArticle.contentFormat || "markdown",
    blocks,
    content: rawArticle.content || rawArticle.markdown || "",
    // Блок с инструментом приходит с бэкенда отдельно от текста: промокод в нём
    // может истечь, и тогда его просто не будет
    telegramUrl: rawArticle.telegram_url || rawArticle.telegramUrl || '',
    cta: rawArticle.cta
      ? {
          toolTitle: rawArticle.cta.tool_title || rawArticle.cta.toolTitle || "",
          note: rawArticle.cta.note || "",
          url: rawArticle.cta.url || "",
          promoCode: rawArticle.cta.promo_code || rawArticle.cta.promoCode || "",
          promoAmount:
            Number(rawArticle.cta.promo_amount ?? rawArticle.cta.promoAmount) || 0,
          validUntil: rawArticle.cta.valid_until || rawArticle.cta.validUntil || "",
        }
      : null,
  };
}

export function buildCategoryCollection(articles) {
  const categoryCounts = new Map();

  (articles || []).forEach((article) => {
    if (!article?.categorySlug) return;
    categoryCounts.set(
      article.categorySlug,
      (categoryCounts.get(article.categorySlug) || 0) + 1
    );
  });

  const categories = Array.from(categoryCounts.entries())
    .map(([slug, count]) => {
      const meta = normalizeCategoryName(slug, slug);
      return {
        slug,
        name: meta.name,
        description: meta.description,
        count,
      };
    })
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name));

  return [
    {
      slug: "all",
      name: "Все",
      description: DEFAULT_CATEGORY_DESCRIPTION,
      count: articles.length,
    },
    ...categories,
  ];
}

function normalizeListingResponse(data, fallbackParams) {
  const items = Array.isArray(data)
    ? data
    : data?.articles || data?.items || data?.results || [];

  const articles = items.map((item) => normalizeBlogArticle(item)).filter(Boolean);
  const perPage = Number(
    data?.per_page || data?.perPage || fallbackParams.perPage || BLOG_PAGE_SIZE
  );
  const total = Number(data?.total || data?.count || articles.length);
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, perPage)));
  const page = Math.min(
    Math.max(1, Number(data?.page || fallbackParams.page || 1)),
    pageCount
  );

  return {
    articles,
    total,
    page,
    perPage,
    pageCount,
    source: "api",
  };
}

function normalizeCategoriesResponse(data) {
  const items = Array.isArray(data) ? data : data?.categories || data?.items || [];
  const categories = items
    .map((item) => {
      const slug = resolveCategorySlug(item.slug || item.name || item.category);
      if (!slug) return null;

      const meta = normalizeCategoryName(item.name || slug, slug);
      return {
        slug,
        name: item.name || meta.name,
        description: meta.description,
        count: Number(item.count || item.article_count || item.total || 0),
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name));

  const total = categories.reduce((sum, category) => sum + category.count, 0);

  return [
    {
      slug: "all",
      name: "Все",
      description: DEFAULT_CATEGORY_DESCRIPTION,
      count: total,
    },
    ...categories,
  ];
}

async function fetchJson(path, init = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Blog API request failed: ${response.status}`);
  }

  return response.json();
}

export async function fetchBlogArticles(params = {}) {
  const {
    page = 1,
    perPage = BLOG_PAGE_SIZE,
    search = "",
    contentType = "",
    category = "",
  } = params;

  const query = new URLSearchParams();
  query.set("page", String(page));
  query.set("per_page", String(perPage));

  if (search) {
    query.set("search", search);
  }

  if (contentType && contentType !== "all") {
    query.set("content_type", contentType);
  }

  const normalizedCategory = resolveCategorySlug(category);
  if (normalizedCategory && normalizedCategory !== "all") {
    query.set("category", normalizedCategory);
  }

  const data = await fetchJson(`/api/v1/blog/articles?${query.toString()}`);
  return normalizeListingResponse(data, { page, perPage });
}

export async function fetchBlogCategories() {
  const data = await fetchJson("/api/v1/blog/categories");
  return normalizeCategoriesResponse(data);
}

export async function fetchBlogArticle(slug) {
  if (!slug) return null;

  const data = await fetchJson(`/api/v1/blog/articles/${slug}`);
  return normalizeBlogArticle(data);
}

export async function fetchRelatedBlogArticles(article, limit = 3) {
  if (!article?.categorySlug) {
    return [];
  }

  try {
    const listing = await fetchBlogArticles({
      category: article.categorySlug,
      page: 1,
      perPage: limit + 3,
    });

    return listing.articles
      .filter((item) => item.slug !== article.slug)
      .slice(0, limit);
  } catch (error) {
    return [];
  }
}

export function getCategoryBySlug(categorySlug, categories = []) {
  const resolvedSlug = resolveCategorySlug(categorySlug);
  return (
    categories.find((category) => category.slug === resolvedSlug) ||
    STATIC_CATEGORY_COLLECTION.find((category) => category.slug === resolvedSlug) ||
    null
  );
}
